import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import { Loading } from './components/ui';

// Pages
import {
  LoginPage,
  DashboardPage,
  MiDepartamentoPage,
  MisPagosPage,
  TransparenciaFinancieraPage,
  IngresosPage,
  EgresosPage,
  DepartamentosPage,
  ProveedoresPage,
  PersonalPage,
  BalancesPage,
  InventarioPage,
  ReservasPage,
  ComunicadosPage,
  VotacionesPage,
  DocumentosPage,
  ControlAccesoPage,
  IncidenciasPage,
  SancionesPage,
  ConfirmarPagosPage,
  RecibosPage,
  UsuariosPage
} from './pages';
import FlujoCajaPage from './pages/FlujoCajaPage';
import AdministracionPage from './pages/AdministracionPage';
import FinanzasPage from './pages/FinanzasPage';
import ComunidadPage from './pages/ComunidadPage';
import OperacionesPage from './pages/OperacionesPage';

// Protected Route Component
const ProtectedRoute = ({ children, roles = [], requiresDepartamento = false }) => {
  const { isAuthenticated, usuario, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loading size="lg" text="Cargando..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Si requiere departamento, verificar que el usuario tenga uno
  if (requiresDepartamento && !usuario?.departamento_id) {
    return <Navigate to="/" replace />;
  }

  // Check role permissions (solo si no requiere departamento)
  if (!requiresDepartamento && roles.length > 0 && !roles.includes(usuario?.rol) && usuario?.rol !== 'ADMIN') {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loading size="lg" text="Cargando..." />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO']}>
            <DashboardPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/mi-departamento"
        element={
          <ProtectedRoute requiresDepartamento={true}>
            <MiDepartamentoPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/mis-pagos"
        element={
          <ProtectedRoute requiresDepartamento={true}>
            <MisPagosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/transparencia-financiera"
        element={
          <ProtectedRoute roles={['PROPIETARIO']}>
            <TransparenciaFinancieraPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/confirmar-pagos"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO']}>
            <ConfirmarPagosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/recibos"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO']}>
            <RecibosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/ingresos"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'PROPIETARIO']}>
            <IngresosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/egresos"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'PROPIETARIO']}>
            <EgresosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/flujo-caja"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'PROPIETARIO']}>
            <FlujoCajaPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/balances"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'PROPIETARIO']}>
            <BalancesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/finanzas"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO']}>
            <FinanzasPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/comunidad"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO']}>
            <ComunidadPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/operaciones"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PORTERO']}>
            <OperacionesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/control-acceso"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'SECRETARIO', 'PORTERO']}>
            <ControlAccesoPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/proveedores"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO']}>
            <ProveedoresPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/administracion"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO']}>
            <AdministracionPage />
          </ProtectedRoute>
        }
      />

      {/* Removed individual routes for Departamentos, Proveedores, Personal, Inventario, Usuarios */}

      <Route
        path="/reservas"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO']}>
            <ReservasPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/comunicados"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO']}>
            <ComunicadosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/votaciones"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO']}>
            <VotacionesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/documentos"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO']}>
            <DocumentosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/control-acceso"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'SECRETARIO', 'PORTERO']}>
            <ControlAccesoPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/incidencias"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO', 'PROPIETARIO']}>
            <IncidenciasPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/sanciones"
        element={
          <ProtectedRoute roles={['ADMIN', 'PRESIDENTE', 'TESORERO', 'SECRETARIO']}>
            <SancionesPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/usuarios"
        element={
          <ProtectedRoute roles={['ADMIN']}>
            <UsuariosPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
